import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  CircularProgress, TextField, Grid, Alert, Card, CardContent
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DFeSearch() {
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    date_from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dfe/documents`, {
        headers: { Authorization: `Bearer ${token}` },
        params: filters
      });
      setDocuments(response.data.data || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  const searchNewDocuments = async () => {
    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/dfe/search`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Encontradas ${response.data.new_documents || 0} novas notas!`);
      fetchDocuments();
    } catch (error) {
      const backendError = error.response?.data?.error;
      const backendMessage = error.response?.data?.message;
      const message = backendError || backendMessage || 'Erro ao buscar notas';

      if (String(message).toLowerCase().includes('menos de 1 hora')) {
        toast.info(message);
      } else {
        toast.error(message);
      }
    } finally {
      setSearching(false);
    }
  };

  const downloadXml = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/dfe/documents/${doc.id}/xml`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blob = new Blob([response.data.xml], { type: 'application/xml' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${doc.access_key}.xml`;
      link.click();
    } catch (error) {
      toast.error('Erro ao baixar XML');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">
          <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Busca Automática de NF-e
        </Typography>
        <Button
          variant="contained"
          startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
          onClick={searchNewDocuments}
          disabled={searching}
        >
          {searching ? 'Buscando...' : 'Buscar Novas Notas'}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Esta funcionalidade busca automaticamente as notas fiscais (NF-e) emitidas contra o CNPJ da sua empresa na SEFAZ.
        As notas são baixadas e armazenadas para consulta.
      </Alert>

      {/* Resumo */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption">Total de Notas</Typography>
                <Typography variant="h4">{summary.total_documents}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption">Este Mês</Typography>
                <Typography variant="h4">{summary.this_month}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption">Valor Total</Typography>
                <Typography variant="h5">{formatCurrency(summary.total_value)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="caption">Última Busca</Typography>
                <Typography variant="body2">
                  {summary.last_search ? new Date(summary.last_search).toLocaleString('pt-BR') : 'Nunca'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filtros */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data Inicial"
              InputLabelProps={{ shrink: true }}
              value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Data Final"
              InputLabelProps={{ shrink: true }}
              value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDocuments}
              fullWidth
            >
              Filtrar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabela de documentos */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Chave de Acesso</TableCell>
              <TableCell>Emitente</TableCell>
              <TableCell>Data Emissão</TableCell>
              <TableCell align="right">Valor</TableCell>
              <TableCell>Situação</TableCell>
              <TableCell>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhuma nota encontrada. Clique em "Buscar Novas Notas" para sincronizar.
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {doc.access_key?.substring(0, 22)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {doc.emitter_name}<br />
                    <Typography variant="caption" color="textSecondary">
                      {doc.emitter_cnpj}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(doc.emission_date).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(doc.total_value)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.situation === 'authorized' ? 'Autorizada' : doc.situation === 'cancelled' ? 'Cancelada' : doc.situation}
                      color={doc.situation === 'authorized' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => downloadXml(doc)} title="Baixar XML">
                      <DownloadIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
